import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { ApiVersions } from '@gonfalon/openapi/api-versions';
import { useQueries } from '@tanstack/react-query';

import type { UsageTabData, UsageTabKey } from '../../types';
import { AggregationType, Granularity, TimeRangeOption, UsageTab } from '../../types';
import {
  getSeriesTotal,
  getSubscriptionLimits,
  getUsageDisplayName,
  resolveQueryFn,
  shouldShowProjection,
} from '../../utils';
import { usageTabConfig } from '../constants/usageTabConfig';
import { DataExportScope, getDataExportChartView, hasBothDataExportSources } from '../utils/combineDataExportUsage';
import { getEffectiveTo } from '../utils/dates';

import { useDataExportSourceCapabilities } from './useDataExportSourceCapabilities';
import { useDataExportSourceUsage } from './useDataExportSourceUsage';
import { useUsageMonthOptions } from './useUsageMonthOptions';
import { useUsageTimestamps } from './useUsageTimestamps';

type UseAllUsageTabDataOptions = {
  isDiagnostic?: boolean;
};

export function useAllUsageTabData(
  tabs: UsageTabKey[],
  aggregationType: AggregationType = AggregationType.MONTH_TO_DATE,
  { isDiagnostic = false }: UseAllUsageTabDataOptions = {},
): UsageTabData[] {
  const { subscription } = useAccountContext();

  const { today, defaultMonth } = useUsageMonthOptions();
  const timestamps = useUsageTimestamps({
    timeRange: TimeRangeOption.MONTH_TO_DATE,
    month: defaultMonth,
    today,
  });

  const singleQueryTabs = tabs.filter((tab) => tab !== UsageTab.DATA_EXPORT_COMBINED);

  // Daily queries for chart data points (all tabs).
  const chartQueries = useQueries({
    queries: singleQueryTabs.map((tab) => {
      const { queryFnName, defaultAggregationType } = usageTabConfig[tab];
      const tabAggregationType = isDiagnostic ? (defaultAggregationType ?? aggregationType) : aggregationType;
      const effectiveTo = getEffectiveTo(timestamps.to, today, tabAggregationType);

      const queryFn = resolveQueryFn(queryFnName);
      return queryFn({
        apiVersion: ApiVersions.Beta,
        query: {
          aggregationType: tabAggregationType,
          from: timestamps.from.toString(),
          to: effectiveTo.toString(),
          granularity: Granularity.DAILY,
        },
      });
    }),
  });

  // Some tabs need a separate query to compute the summary total (e.g. metrics
  // uses MONTH_TO_DATE to get a cumulative value instead of deriving it from
  // the AVERAGE chart series). Skip when the chart query already uses the override type.
  const overrideTabs = singleQueryTabs.filter((tab) => {
    const { totalAggregationTypeOverride, defaultAggregationType } = usageTabConfig[tab];
    const tabAggregationType = isDiagnostic ? (defaultAggregationType ?? aggregationType) : aggregationType;
    return totalAggregationTypeOverride !== undefined && totalAggregationTypeOverride !== tabAggregationType;
  });

  const totalQueries = useQueries({
    queries: overrideTabs.map((tab) => {
      const { queryFnName, totalAggregationTypeOverride } = usageTabConfig[tab];
      const queryFn = resolveQueryFn(queryFnName);
      return queryFn({
        apiVersion: ApiVersions.Beta,
        query: {
          aggregationType: totalAggregationTypeOverride ?? aggregationType,
          from: timestamps.from.toString(),
          to: timestamps.to.toString(),
          granularity: Granularity.DAILY,
        },
      });
    }),
  });

  const limits = getSubscriptionLimits(subscription);

  const combinedAggregationType = isDiagnostic
    ? (usageTabConfig[UsageTab.DATA_EXPORT_COMBINED].defaultAggregationType ?? aggregationType)
    : aggregationType;
  const combinedSources = useDataExportSourceUsage({
    from: timestamps.from,
    to: getEffectiveTo(timestamps.to, today, combinedAggregationType),
    aggregationType: combinedAggregationType,
    granularity: Granularity.DAILY,
    enabled: singleQueryTabs.length !== tabs.length,
  });
  const capabilities = useDataExportSourceCapabilities({ enabled: singleQueryTabs.length !== tabs.length });

  return tabs.map((tab) => {
    if (tab === UsageTab.DATA_EXPORT_COMBINED) {
      const { limitName, precision } = usageTabConfig[tab];
      const view = getDataExportChartView({
        sources: combinedSources,
        scope: DataExportScope.BOTH,
        aggregationType: combinedAggregationType,
        granularity: Granularity.DAILY,
        timestamps,
        hasBothSources: hasBothDataExportSources(capabilities, combinedSources),
      });
      return {
        data: { data: view.status === 'ready' ? view.data : undefined },
        isLoading: view.status === 'loading',
        isError: view.status === 'error',
        tab,
        timestamps,
        limitName,
        limit: limits?.[limitName] ?? 0,
        title: getUsageDisplayName(tab, 'longTitleName'),
        total: view.status === 'ready' ? view.total : 0,
        precision,
        aggregationType: combinedAggregationType,
        hasProjection: false,
        ...(view.status === 'ready' && { tooltipComponents: view.tooltipComponents }),
      };
    }

    const chartQuery = chartQueries[singleQueryTabs.indexOf(tab)];
    const { limitName, precision, defaultAggregationType } = usageTabConfig[tab];
    const tabAggregationType = isDiagnostic ? (defaultAggregationType ?? aggregationType) : aggregationType;

    const limit = limitName ? (limits?.[limitName] ?? 0) : 0;
    const title = getUsageDisplayName(tab, 'titleName');

    const { totalAggregationTypeOverride } = usageTabConfig[tab];
    const overrideIdx = overrideTabs.indexOf(tab);
    const totalQuery = overrideIdx !== -1 ? totalQueries[overrideIdx] : undefined;

    const hasProjection = shouldShowProjection(
      tab,
      tabAggregationType,
      Granularity.DAILY,
      chartQuery.data?.projectedSeries?.length ?? 0,
    );

    const total = totalQuery
      ? getSeriesTotal({
          series: totalQuery.data?.series ?? [],
          aggregationType: totalAggregationTypeOverride ?? tabAggregationType,
          granularity: Granularity.DAILY,
        })
      : getSeriesTotal({
          series: chartQuery.data?.series ?? [],
          aggregationType: tabAggregationType,
          granularity: Granularity.DAILY,
        });

    return {
      data: chartQuery,
      isLoading: chartQuery.isLoading || (totalQuery?.isLoading ?? false),
      isError: chartQuery.isError || (totalQuery?.isError ?? false),
      tab,
      timestamps,
      limitName,
      limit,
      title,
      total,
      precision,
      aggregationType: tabAggregationType,
      hasProjection,
    };
  });
}
